import { useState } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import axios from "axios";
import ArticleEditor from "./Blogs/BlogEditor";

export default function EditBlogForm({ blog, onCancel, onSuccess }) {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [formData, setFormData] = useState({
    type: blog.type || "blog",
    title: blog.title || "",
    author: blog.author || "",
    date: blog.date || "",
    tag: blog.tag || "",
    timeToRead: blog.timeToRead || "",
    link: blog.link || "",
    summary: blog.summary || "",
    content: blog.type === "blog" ? blog.content || "" : "",
  });

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files && files[0]) {
      setSelectedImageFile(files[0]);
    } else if (name === "content_pdf" && files && files[0]) {
      setSelectedPdfFile(files[0]);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const removeImageFile = () => setSelectedImageFile(null);
  const removePdfFile = () => setSelectedPdfFile(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = new FormData();

    const blogData = {
      type: formData.type,
      title: formData.title,
      author: formData.author,
      date: formData.date,
      tag: formData.tag,
      timeToRead: formData.timeToRead,
      link: formData.link && formData.link.trim() !== "" ? formData.link : null,
      summary: formData.summary,
      content: formData.type === "blog" ? formData.content : null,
    };

    payload.append("blog_data", JSON.stringify(blogData));

    if (selectedImageFile) {
      payload.append("image", selectedImageFile);
    }
    if (selectedPdfFile) {
      payload.append("content_pdf", selectedPdfFile);
    }

    try {
      const res = await axios.put(`${API_BASE}blogs/${blog.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert(res.data.message);
      onSuccess();
    } catch (err) {
      alert(
        "Error updating blog: " +
          (err.response?.data?.detail || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    { name: "title", label: "Title", type: "text", placeholder: "Blog title" },
    { name: "author", label: "Author", type: "text", placeholder: "Author name" },
    { name: "date", label: "Date", type: "date", placeholder: "" },
    { name: "tag", label: "Tag", type: "text", placeholder: "Enter tag" },
    { name: "timeToRead", label: "Time to Read", type: "text", placeholder: "5 min" },
    { name: "link", label: "Link", type: "text", placeholder: "https://..." },
    { name: "summary", label: "Summary", type: "textarea", placeholder: "Brief summary..." },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Edit {formData.type === "blog" ? "Blog" : "Whitepaper"}</h2>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-sm bg-gray-300 rounded hover:bg-gray-400 cursor-pointer"
        >
          Cancel
        </button>
      </div>

      <div>
        <label htmlFor="type" className="block font-medium mb-1">Type</label>
        <select
          name="type"
          value={formData.type}
          onChange={handleChange}
          className="block w-full mb-2 p-2 border rounded"
        >
          <option value="blog">Blog</option>
          <option value="whitepaper">Whitepaper</option>
        </select>
      </div>

      {fields.map(({ name, label, type, placeholder }) => (
        <div key={name}>
          <label htmlFor={name} className="block font-medium mb-1">{label}</label>
          {type === "textarea" ? (
            <textarea
              name={name}
              placeholder={placeholder}
              value={formData[name] || ""}
              onChange={handleChange}
              rows={4}
              className="block w-full mb-2 p-2 border rounded"
            />
          ) : (
            <input
              type={type}
              name={name}
              placeholder={placeholder}
              value={formData[name] || ""}
              onChange={handleChange}
              className="block w-full mb-2 p-2 border rounded"
              required={name !== "link"}
            />
          )}
        </div>
      ))}

      {formData.type === "blog" && (
        <div className="mb-4">
          <label className="block font-medium mb-1">Content</label>
          <ArticleEditor
            content={formData.content}
            setContent={(html) => setFormData({ ...formData, content: html })}
          />
        </div>
      )}

      {formData.type !== "blog" && (
        <>
          <label className="block font-medium mb-1">Replace Content PDF</label>
          <div className="flex items-center gap-3 mb-4">
            <label
              htmlFor="editPdfUpload"
              className="flex items-center gap-2 px-4 py-2 border rounded cursor-pointer text-blue-600 hover:bg-blue-50"
            >
              <FaCloudUploadAlt className="text-xl" />
              {selectedPdfFile ? "Change File" : "Upload New PDF"}
            </label>
            <input
              id="editPdfUpload"
              name="content_pdf"
              type="file"
              accept=".pdf"
              onChange={handleChange}
              className="hidden"
            />
            {selectedPdfFile && (
              <>
                <span className="text-sm text-gray-700">{selectedPdfFile.name}</span>
                <button
                  onClick={removePdfFile}
                  type="button"
                  className="text-red-500 text-sm underline"
                >
                  Remove
                </button>
              </>
            )}
            {!selectedPdfFile && (
              <span className="text-sm text-gray-400">Keep existing PDF if not replaced</span>
            )}
          </div>
        </>
      )}

      <label className="block font-medium mb-1">Replace Image</label>
      <div className="flex items-center gap-3 mb-4">
        <label
          htmlFor="editImageUpload"
          className="flex items-center gap-2 px-4 py-2 border rounded cursor-pointer text-blue-600 hover:bg-blue-50"
        >
          <FaCloudUploadAlt className="text-xl" />
          {selectedImageFile ? "Change File" : "Upload New Image"}
        </label>
        <input
          id="editImageUpload"
          name="image"
          type="file"
          onChange={handleChange}
          className="hidden"
        />
        {selectedImageFile ? (
          <>
            <span className="text-sm text-gray-700">{selectedImageFile.name}</span>
            <button
              onClick={removeImageFile}
              type="button"
              className="text-red-500 text-sm underline"
            >
              Remove
            </button>
          </>
        ) : (
          <span className="text-sm text-gray-400">Keep existing image if not replaced</span>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="bg-blue-600 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? "Updating..." : "Update"}
      </button>
    </form>
  );
}
